import { AssetPermission, PermissionName } from '../types/asset-permissions.types'
import { EntityTypes } from '../../../redux/slices/modal'
import { OrganizationSearchField } from './OrganizationSearchField'
import {
  SearchResult,
  SearchResultType,
  TeamMemberResult,
  TeamResult
} from './OrganizationSearchField/OrganizationSearchField.types'
import { TeamMemberRole } from '@dtx-company/true-common/src/types/org'
import { fireHeapEvent } from '@dtx-company/inter-app/src/event-tracking/helpers'
import { useAssetPermissionsState } from '../hooks/asset-permissions/useAssetPermissionsState'
import React, { useCallback } from 'react'
import eventNames from '@dtx-company/inter-app/src/event-tracking/constants/domainsV2'

export interface ShareAssetSearchFieldProps {
  assetId: string
  isWhitelabel?: boolean
  isCodeSubdomain?: boolean
  label?: string
  searchFieldType?: 'v1' | 'v2'
  trackingCall?: () => void
}

export const ShareAssetSearchField = ({
  assetId,
  isWhitelabel,
  isCodeSubdomain,
  trackingCall,
  label,
  searchFieldType
}: ShareAssetSearchFieldProps): JSX.Element => {
  const state = useShareAssetSearchFieldState(assetId, isWhitelabel, isCodeSubdomain)
  return (
    <OrganizationSearchField
      removeFromResults={state.resultsToRemove}
      searchFor="membersAndTeams"
      onSearchResultClicked={state.onClickSearchResult}
      label={label || 'Enter the team name, member name or email below'}
      placeholder=""
      onFocus={trackingCall}
      onRefInitialized={() => undefined}
      searchInputType={searchFieldType}
    />
  )
}

interface ShareAssetSearchFieldState {
  resultsToRemove: SearchResult[]
  onClickSearchResult: (searchResult: SearchResult) => void
  isWhitelabel?: boolean
  isCodeSubdomain?: boolean
}

function useShareAssetSearchFieldState(
  assetId: string,
  isWhitelabel?: boolean,
  isCodeSubdomain?: boolean
): ShareAssetSearchFieldState {
  const assetPermissionsState = useAssetPermissionsState(assetId, isWhitelabel, isCodeSubdomain)

  const resultsToRemove = getSearchResultsFromPermissions(
    assetPermissionsState?.draftPermissions ?? []
  )
  const onClickSearchResult = useCallback(
    (searchResult: SearchResult): void => {
      const permission = getPermissionFromSearchResult(searchResult)
      if (permission) {
        assetPermissionsState?.addDraftPermission(permission)
      }
      if (isWhitelabel) {
        const { Core_Domain_Settings_Selected_Team } = eventNames
        fireHeapEvent(Core_Domain_Settings_Selected_Team)
      }
    },
    [assetPermissionsState, isWhitelabel]
  )

  return {
    resultsToRemove,
    onClickSearchResult
  }
}

function getSearchResultsFromPermissions(permissions: AssetPermission[]): SearchResult[] {
  const searchResults = permissions.map(p => {
    if (p.entityType === EntityTypes.USER) {
      return getMemberResultFromPermission(p)
    }
    if (p.entityType === EntityTypes.TEAM) {
      return getTeamResultFromPermission(p)
    }

    return null
  })

  return searchResults.filter(s => s !== null) as SearchResult[]
}

function getMemberResultFromPermission(permission: AssetPermission): TeamMemberResult {
  return {
    type: SearchResultType.TEAM_MEMBER,
    id: permission.entityId,
    name: permission.entityName,
    role: TeamMemberRole.MEMBER, //dummy value, this is required in the TeamMemberResult type but not available on collaborators
    hasBeenInvited: true,
    hasJoinedOrg: true
  }
}

function getTeamResultFromPermission(permission: AssetPermission): TeamResult {
  return {
    type: SearchResultType.TEAM,
    teamId: permission.entityId,
    name: permission.entityName,
    memberCount: 0, //dummy value, this is required in the TeamMemberResult type but not available on collaborators
    directChildrenCount: 0 //dummy value, this is required in the TeamMemberResult type but not available on collaborators
  }
}

function getPermissionFromSearchResult(searchResult: SearchResult): AssetPermission | null {
  if (searchResult.type === SearchResultType.TEAM_MEMBER) {
    return getPermissionFromTeamMemberResult(searchResult)
  }

  if (searchResult.type === SearchResultType.TEAM) {
    return getPermissionFromTeamResult(searchResult)
  }

  return null
}

function getPermissionFromTeamMemberResult(teamMemberResult: TeamMemberResult): AssetPermission {
  return {
    entityId: teamMemberResult?.id,
    entityName: teamMemberResult?.name ? teamMemberResult?.name : teamMemberResult?.email ?? '',
    entityType: EntityTypes.USER,
    permissionName: PermissionName.EDITOR //default value for a new collaborator
  }
}

function getPermissionFromTeamResult(teamResult: TeamResult): AssetPermission {
  return {
    entityId: teamResult?.teamId,
    entityName: teamResult?.name ?? '',
    entityType: EntityTypes.TEAM,
    permissionName: PermissionName.EDITOR //default value for a new collaborator
  }
}
